export type RgbColor = {
  r: number
  g: number
  b: number
}

export type HslColor = {
  h: number
  s: number
  l: number
}

export type CampaignBrandPalette = {
  primary: string
  dark: string
  darker: string
  deep: string
  light: string
  accent: string
  sliceA: string
  sliceB: string
  rgb: RgbColor
  accentRgb: RgbColor
  darkRgb: RgbColor
  muteBg: string
  textTitle: string
  textMuted: string
  iconBg: string
  iconFg: string
}

const DEFAULT_BRAND_HEX = '#5e17eb'

export function normalizeHexColor(value: string | null | undefined): string | null {
  const raw = String(value ?? '').trim()
  if (!raw) {
    return null
  }

  const withHash = raw.startsWith('#') ? raw : `#${raw}`
  const body = withHash.slice(1)

  if (body.length === 3 && /^[0-9a-fA-F]{3}$/.test(body)) {
    return `#${body
      .split('')
      .map((character) => character + character)
      .join('')
      .toLowerCase()}`
  }

  if (body.length === 6 && /^[0-9a-fA-F]{6}$/.test(body)) {
    return `#${body.toLowerCase()}`
  }

  return null
}

export function hexToRgb(hex: string | null | undefined): RgbColor {
  const normalized = normalizeHexColor(hex)
  if (!normalized) {
    return { r: 94, g: 23, b: 235 }
  }

  const body = normalized.slice(1)
  return {
    r: Number.parseInt(body.slice(0, 2), 16),
    g: Number.parseInt(body.slice(2, 4), 16),
    b: Number.parseInt(body.slice(4, 6), 16),
  }
}

export function rgbToHex(red: number, green: number, blue: number): string {
  const channel = (value: number) => {
    const clamped = Math.max(0, Math.min(255, Math.round(value)))
    return clamped.toString(16).padStart(2, '0')
  }

  return `#${channel(red)}${channel(green)}${channel(blue)}`
}

export function rgbToHsl(red: number, green: number, blue: number): HslColor {
  const redNormalized = red / 255
  const greenNormalized = green / 255
  const blueNormalized = blue / 255
  const max = Math.max(redNormalized, greenNormalized, blueNormalized)
  const min = Math.min(redNormalized, greenNormalized, blueNormalized)
  let hue = 0
  let saturation = 0
  const lightness = (max + min) / 2

  if (max !== min) {
    const delta = max - min
    saturation = lightness > 0.5 ? delta / (2 - max - min) : delta / (max + min)

    switch (max) {
      case redNormalized:
        hue = (greenNormalized - blueNormalized) / delta + (greenNormalized < blueNormalized ? 6 : 0)
        break
      case greenNormalized:
        hue = (blueNormalized - redNormalized) / delta + 2
        break
      default:
        hue = (redNormalized - greenNormalized) / delta + 4
    }

    hue /= 6
  }

  return { h: hue * 360, s: saturation * 100, l: lightness * 100 }
}

export function hslToRgb(hue: number, saturation: number, lightness: number): RgbColor {
  const hueDegrees = ((hue % 360) + 360) % 360
  const saturationNormalized = Math.max(0, Math.min(100, saturation)) / 100
  const lightnessNormalized = Math.max(0, Math.min(100, lightness)) / 100

  if (saturationNormalized === 0) {
    const gray = Math.round(lightnessNormalized * 255)
    return { r: gray, g: gray, b: gray }
  }

  const temporaryOne =
    lightnessNormalized < 0.5
      ? lightnessNormalized * (1 + saturationNormalized)
      : lightnessNormalized + saturationNormalized - lightnessNormalized * saturationNormalized
  const temporaryTwo = 2 * lightnessNormalized - temporaryOne

  const hueToRgb = (start: number, end: number, offset: number) => {
    let adjustedOffset = offset
    if (adjustedOffset < 0) adjustedOffset += 1
    if (adjustedOffset > 1) adjustedOffset -= 1
    if (adjustedOffset < 1 / 6) return start + (end - start) * 6 * adjustedOffset
    if (adjustedOffset < 1 / 2) return end
    if (adjustedOffset < 2 / 3) return start + (end - start) * (2 / 3 - adjustedOffset) * 6
    return start
  }

  return {
    r: Math.round(hueToRgb(temporaryTwo, temporaryOne, hueDegrees / 360 + 1 / 3) * 255),
    g: Math.round(hueToRgb(temporaryTwo, temporaryOne, hueDegrees / 360) * 255),
    b: Math.round(hueToRgb(temporaryTwo, temporaryOne, hueDegrees / 360 - 1 / 3) * 255),
  }
}

export function hslHex(hue: number, saturation: number, lightness: number): string {
  const rgb = hslToRgb(hue, saturation, lightness)
  return rgbToHex(rgb.r, rgb.g, rgb.b)
}

/** Mesma paleta derivada usada em `backend/public/js/widget.js`. */
export function buildCampaignBrandPalette(primaryHex: string | null | undefined): CampaignBrandPalette {
  const primary = normalizeHexColor(primaryHex) ?? DEFAULT_BRAND_HEX
  const rgb = hexToRgb(primary)
  const hsl = rgbToHsl(rgb.r, rgb.g, rgb.b)
  const hue = hsl.h
  const saturation = hsl.s
  const lightness = hsl.l

  const dark = hslHex(hue, Math.min(100, saturation + 5), Math.max(10, lightness - 24))
  const darker = hslHex(hue, Math.min(100, saturation + 8), Math.max(8, lightness - 32))
  const deep = hslHex(hue, Math.min(100, saturation + 10), Math.max(6, lightness - 40))
  const light = hslHex(hue, Math.min(100, saturation + 15), Math.min(55, lightness + 20))
  const accent = hslHex(hue, Math.min(100, saturation + 20), Math.min(50, lightness + 12))
  const sliceA = hslHex(hue, Math.min(92, saturation * 0.92), Math.min(46, Math.max(30, lightness + 4)))
  const sliceB = hslHex(hue, Math.min(92, saturation * 0.88), Math.max(24, lightness - 12))

  return {
    primary,
    dark,
    darker,
    deep,
    light,
    accent,
    sliceA,
    sliceB,
    rgb,
    accentRgb: hexToRgb(accent),
    darkRgb: hexToRgb(dark),
    muteBg: hslHex(hue, Math.min(35, saturation * 0.2), 97),
    textTitle: hslHex(hue, Math.min(28, saturation * 0.45), 16),
    textMuted: hslHex(hue, Math.min(22, saturation * 0.22), 42),
    iconBg: hslHex(hue, Math.min(40, saturation * 0.25), 95),
    iconFg: hslHex(hue, Math.min(100, saturation + 10), Math.max(22, lightness - 18)),
  }
}

export function rgbaFromRgb(rgb: RgbColor, alpha: number): string {
  return `rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, ${alpha})`
}

export function resolveCampaignBrandColor(
  primaryColor: string | null | undefined,
  buttonColor: string | null | undefined
): string {
  return (
    normalizeHexColor(primaryColor) ??
    normalizeHexColor(buttonColor) ??
    DEFAULT_BRAND_HEX
  )
}

export function resolveCampaignButtonTextColor(value: string | null | undefined): string {
  return normalizeHexColor(value) ?? '#ffffff'
}

export function campaignBrandPaletteToStyle(
  palette: CampaignBrandPalette,
  buttonTextColor: string = '#ffffff'
): Record<string, string> {
  const shareGradient = `linear-gradient(180deg, ${palette.light} 0%, ${palette.primary} 45%, ${palette.deep} 100%)`
  const wrapperGradient = `linear-gradient(135deg, ${palette.primary} 0%, ${palette.dark} 50%, ${palette.accent} 100%)`
  const pointerGradient = `radial-gradient(${palette.accent}, ${palette.primary})`
  const centerGradient = `radial-gradient(circle at 30% 30%, ${palette.accent} 0%, ${palette.deep} 100%)`
  const shareShadow = `0 6px 22px ${rgbaFromRgb(palette.darkRgb, 0.45)}, 0 2px 6px rgba(0, 0, 0, 0.12)`

  return {
    '--wheel-brand-primary': palette.primary,
    '--wheel-brand-dark': palette.dark,
    '--wheel-brand-darker': palette.darker,
    '--wheel-brand-deep': palette.deep,
    '--wheel-brand-light': palette.light,
    '--wheel-brand-accent': palette.accent,
    '--wheel-brand-slice-a': palette.sliceA,
    '--wheel-brand-slice-b': palette.sliceB,
    '--wheel-brand-text-title': palette.textTitle,
    '--wheel-brand-text-muted': palette.textMuted,
    '--wheel-brand-button-text': buttonTextColor,
    '--wheel-brand-share-gradient': shareGradient,
    '--wheel-brand-wrapper-gradient': wrapperGradient,
    '--wheel-brand-pointer-gradient': pointerGradient,
    '--wheel-brand-center-gradient': centerGradient,
    '--wheel-brand-share-shadow': shareShadow,
    '--wheel-brand-wrapper-ring': palette.dark,
    '--wheel-brand-wrapper-glow': rgbaFromRgb(palette.rgb, 0.3),
    '--wheel-brand-light-glow': rgbaFromRgb(palette.accentRgb, 0.85),
    '--wheel-brand-success-bg': rgbaFromRgb(palette.rgb, 0.1),
    '--wheel-brand-center-hover-glow': rgbaFromRgb(palette.darkRgb, 0.5),
  }
}
